import { useState } from "react";
import { reviews } from "@/mocks/reviews";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "motion/react";

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

export default function ReviewSection() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const review = reviews[currentIndex];

  const goNext = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-80px" }}
      variants={sectionVariants}
      className="w-full py-16 md:py-20 px-4 md:px-6 bg-[#FAFAF8]"
    >
      <div className="max-w-[1280px] mx-auto">
        {/* Section Header */}
        <motion.div variants={fadeUpVariants} className="text-center mb-12">
          <span className="inline-block px-3 py-1 text-xs font-medium bg-[#C4956A]/10 text-[#C4956A] rounded-full mb-3">
            수강생 이야기
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-[#1C1917] mb-3">
            수강 후기
          </h2>
          <p className="text-sm text-[#8C8780] max-w-[500px] mx-auto">
            강스터디와 함께한 수강생들의 생생한 투자 성공 스토리
          </p>
        </motion.div>

        {/* Featured Review Card */}
        <motion.div
          variants={fadeUpVariants}
          className="bg-[#1C1917] rounded-2xl overflow-hidden"
        >
          <div className="flex flex-col lg:flex-row">
            {/* Left - Avatar */}
            <div className="lg:w-1/3 relative">
              <div className="h-64 lg:h-full min-h-[280px] relative overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img loading="lazy" decoding="async"
                    key={review.id}
                    src={review.avatar}
                    alt={review.name}
                    initial={{ opacity: 0, scale: 1.05 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4, ease: "easeInOut" as const }}
                    className="w-full h-full object-cover object-top absolute inset-0"
                  />
                </AnimatePresence>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1C1917] to-transparent h-24 lg:hidden"></div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="lg:w-2/3 p-8 md:p-12 flex flex-col justify-center">
              <div className="text-6xl text-[#C4956A] leading-none mb-4 font-serif">
                &ldquo;
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.35, ease: "easeInOut" as const }}
                >
                  <p className="text-base md:text-lg text-white/90 leading-relaxed mb-6">
                    {review.content}
                  </p>
                  <div className="mb-6">
                    <p className="text-white font-bold text-base">{review.name}</p>
                    <p className="text-sm text-white/50 mt-0.5">{review.role}</p>
                  </div>

                  {/* Stars */}
                  <div className="flex items-center gap-1 mb-8">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${
                          i < review.rating
                            ? "ri-star-fill text-[#C4956A]"
                            : "ri-star-line text-white/20"
                        }`}
                      ></i>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex items-center gap-3">
                <button
                  onClick={goPrev}
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all"
                  aria-label="이전 후기"
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <button
                  onClick={goNext}
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center text-white/60 hover:text-white hover:border-white/40 transition-all"
                  aria-label="다음 후기"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
                <span className="text-sm text-white/40 ml-2">
                  {currentIndex + 1} / {reviews.length}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Thumbnail Strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="flex items-center justify-center gap-2 mt-6"
        >
          {reviews.map((r, idx) => (
            <motion.button
              key={r.id}
              onClick={() => setCurrentIndex(idx)}
              whileHover={{ scale: 1.15 }}
              whileTap={{ scale: 0.9 }}
              className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-all ${
                idx === currentIndex
                  ? "border-[#C4956A] scale-110"
                  : "border-transparent opacity-50 hover:opacity-80"
              }`}
              aria-label={`${r.name} 후기 보기`}
            >
              <img loading="lazy" decoding="async"
                src={r.avatar}
                alt={r.name}
                className="w-full h-full object-cover"
              />
            </motion.button>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6, ease: "easeOut" as const }}
          className="text-center mt-10"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <Link
              to="/community/reviews"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#1C1917] text-[#1C1917] text-sm font-medium rounded-full hover:bg-[#1C1917] hover:text-white transition-all"
            >
              더 많은 후기 보기
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
}